import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ASSETS, SERVICES, COMPANY_INFO } from '../config/assets';
import { CheckCircle, DollarSign, Clock, Wrench, AlertTriangle, ArrowRight, Diamond, Play, Mail, Phone, MapPin, Send, Zap, ChevronLeft, ChevronRight, Share } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const GrindAndSealPage = () => {
  const service = SERVICES.find(s => s.slug === 'grind-and-seal');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    serviceType: 'Grind and Seal'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // All Grind & Seal project images from the previous website + new uploads
  const allGrindSealImages = [
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/e78v9w64_IMG-20250225-WA0150.jpg', title: 'Residential Full Aggregate Grind & Seal', description: 'Full exposed aggregate finish residential with water based topical sealer satin finish' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/gjjiju8t_IMG-20250225-WA0052.jpg', title: 'Open Living Grind & Seal Project', description: 'Full exposed aggregate finish residential with water based topical sealer satin finish' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/g3ww9zvs_IMG-20250225-WA0040.jpg', title: 'Modern Home Grind & Seal', description: 'Full exposed aggregate finish residential with water based topical sealer satin finish' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/3v4shyx3_IMG-20250225-WA0042.jpg', title: 'Residential Satin Grind & Seal', description: 'Full exposed aggregate finish residential with water based topical sealer satin finish' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/j89bbl8u_IMG-20250225-WA0049.jpg', title: 'Full Aggregate Residential Grind & Seal', description: 'Full exposed aggregate finish residential with water based topical sealer satin finish' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/8x6yxj6t_IMG-20250225-WA0098.jpg', title: 'Residential Full Aggregate Grind & Seal', description: 'Full exposed aggregate finish residential with water based topical sealer satin finish' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/47rz0rp3_IMG-20250225-WA0094.jpg', title: 'Modern Living Area Grind & Seal', description: 'Full exposed aggregate residential grind and seal with satin water based sealer' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/1nb5hr7k_IMG-20250225-WA0092.jpg', title: 'Open Plan Grind & Seal', description: 'Residential full aggregate exposure with protective water based topical sealer' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/dpxxkww3_IMG-20250225-WA0090.jpg', title: 'Satin Finish Residential Grind & Seal', description: 'Full exposed aggregate finish with satin water based topical sealer coating' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/q1qa3jon_IMG-20250225-WA0088.jpg', title: 'Standard Concrete Aggregate Grind & Seal', description: 'Residential full exposed aggregate grind and seal with water based satin finish' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/mb6s5xez_IMG-20250225-WA0027.jpg', title: 'Kitchen White Concrete Grind & Seal', description: 'White concrete decorative full aggregate finish with satin topical sealer' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/ve7sknin_IMG-20250225-WA0022.jpg', title: 'Modern Kitchen Grind & Seal', description: 'Full aggregate exposure grind and seal with white concrete decorative finish' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/f61i2d1f_IMG-20250225-WA0029.jpg', title: 'White Concrete Full Aggregate Grind & Seal', description: 'Decorative white concrete grind and seal with protective topical sealer' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/g9xxgnxn_IMG-20250225-WA0023.jpg', title: 'Residential Kitchen Grind & Seal', description: 'Full aggregate exposure white concrete grind and seal finish' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/oyc9b3zr_IMG-20250225-WA0036.jpg', title: 'White Concrete Full Aggregate Grind & Seal', description: 'White concrete decorative full aggregate exposure with topical sealer' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/xzk9auir_IMG-20250225-WA0033.jpg', title: 'Kitchen Area Grind & Seal', description: 'Full aggregate exposure grind and seal with decorative white concrete finish' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/r587n8b4_IMG-20250225-WA0031.jpg', title: 'Residential Grind & Seal Excellence', description: 'Premium grind and seal with white concrete full aggregate exposure' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/i95bvkrn_IMG-20250225-WA0027.jpg', title: 'Satin Finish Grind & Seal', description: 'Decorative grind and seal satin finish with full aggregate visibility' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/9pltdmtl_IMG-20250225-WA0022.jpg', title: 'Kitchen Area Grind & Seal Project', description: 'White concrete grind and seal with full decorative aggregate exposure' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/8poztfde_IMG-20250302-WA0014.jpg', title: 'Commercial Grind & Seal Project', description: 'Grind and seal with minimal aggregate exposure finish' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/s1z1xbaa_IMG-20250302-WA0012.jpg', title: 'Warehouse Grind & Seal', description: 'Full aggregate exposure grind and seal with topical sealer' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/ywtrx2u0_IMG-20250225-WA0086.jpg', title: 'Medium Aggregate Grind & Seal', description: 'Medium aggregate exposure with protective topical sealer coating' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/fxnf1qkj_IMG-20250225-WA0049.jpg', title: 'Salt & Pepper Grind & Seal', description: 'Salt and pepper aggregate exposure grind and seal finish' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/kxd9ahwt_IMG-20250225-WA0042.jpg', title: 'Full Exposure Grind & Seal', description: 'Full aggregate exposure grind and seal with high-quality topical sealer' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211115_020331-scaled.jpg', title: 'Grind & Seal Installation', description: 'Professional grind and seal process in action' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211115_020350-scaled.jpg', title: 'Surface Preparation', description: 'Initial grinding stage for grind and seal' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211115_0205500-scaled.jpg', title: 'Equipment in Operation', description: 'Professional grinding equipment creating satin finish' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211213_225422-scaled.jpg', title: 'Commercial Grind & Seal', description: 'Large commercial space with sealed finish' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211213_225521-scaled.jpg', title: 'Sealer Application Process', description: 'Applying protective topical sealer coating' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211213_225548-scaled.jpg', title: 'Aggregate Exposure Detail', description: 'Grinding to expose desired aggregate level' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211213_225634-scaled.jpg', title: 'Warehouse Floor Project', description: 'Industrial grind and seal installation' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211213_225642-scaled.jpg', title: 'Progress Documentation', description: 'Mid-process grinding and preparation work' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211213_225649-scaled.jpg', title: 'Surface Quality Check', description: 'Quality control during grinding process' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211213_225716-scaled.jpg', title: 'Equipment Detail View', description: 'Professional grinding machinery close-up' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211213_225735-scaled.jpg', title: 'Team at Work', description: 'Professional team completing grind and seal' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211213_225802-scaled.jpg', title: 'Surface Texture Creation', description: 'Creating optimal surface for sealer adhesion' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211213_225813-scaled.jpg', title: 'Grinding Pattern Work', description: 'Systematic grinding pattern for even finish' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211213_225946-scaled.jpg', title: 'Project Area Overview', description: 'Large area grind and seal completion' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211213_225958-scaled.jpg', title: 'Finished Surface Quality', description: 'Completed grind and seal showing satin finish' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211213_230004-scaled.jpg', title: 'Final Inspection', description: 'Quality verification of sealed surface' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211213_230036-scaled.jpg', title: 'Residential Garage Project', description: 'Home garage grind and seal installation' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211213_230046-scaled.jpg', title: 'Equipment Positioning', description: 'Professional equipment setup for grinding' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211213_230058-scaled.jpg', title: 'Dust Control System', description: 'HEPA filtration during grinding operation' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211213_230152-scaled.jpg', title: 'Surface Grinding Detail', description: 'Close-up of diamond grinding process' }
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allGrindSealImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + allGrindSealImages.length) % allGrindSealImages.length);
  };

  const goToSlide = (index) => {
    setCurrentImageIndex(index);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Thank you! Your message has been sent successfully.');
        setFormData({ name: '', email: '', phone: '', message: '', serviceType: 'Grind and Seal' });
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const keyFeatures = [
    { icon: DollarSign, title: 'Cost-Effective Entry Point', description: 'Lower upfront investment compared to mechanically polished concrete' },
    { icon: Zap, title: 'Quick Installation', description: 'Faster completion times for projects with tight deadlines' },
    { icon: Wrench, title: 'Versatile Application', description: 'Suitable for various concrete conditions and environments' },
    { icon: Clock, title: 'Immediate Results', description: 'Instant transformation with protective sealer coating' }
  ];

  const idealApplications = [
    'Warehouses and industrial facilities',
    'Retail spaces with moderate foot traffic',
    'Residential garages and basements',
    'Commercial kitchens and back-of-house areas',
    'Showrooms with budget constraints',
    'Temporary or short-term commercial spaces'
  ];

  const honestComparison = [
    { aspect: 'Initial Cost', grindSeal: 'Lower upfront investment', mpc: 'Higher initial cost', winner: 'grind' },
    { aspect: 'Long-term Cost', grindSeal: 'Requires resealing every 3-5 years', mpc: '10+ years with minimal maintenance', winner: 'mpc' },
    { aspect: 'Durability', grindSeal: 'Topical sealer can scratch and wear', mpc: 'Integral polish resists damage', winner: 'mpc' },
    { aspect: 'Installation Time', grindSeal: 'Faster completion', mpc: 'More time-intensive process', winner: 'grind' },
    { aspect: 'Appearance', grindSeal: 'Good finish with sealer sheen', mpc: 'Superior mirror-like reflection', winner: 'mpc' },
    { aspect: 'Maintenance', grindSeal: 'Regular resealing required', mpc: 'Low maintenance over lifetime', winner: 'mpc' }
  ];

  const technicalDetails = [
    { title: 'Surface Preparation', description: 'Same initial diamond grinding process as MPC to desired aggregate exposure' },
    { title: 'Sealer Application', description: 'High-quality topical sealer provides protective coating and attractive sheen' },
    { title: 'Finish Options', description: 'Various aggregate exposure levels from salt-and-pepper to heavy aggregate' },
    { title: 'Maintenance Cycle', description: 'Periodic resealing every 3-5 years depending on traffic and usage' },
    { title: 'Chemical Resistance', description: 'Good resistance to oils, chemicals, and stains when properly maintained' },
    { title: 'Slip Resistance', description: 'Can be adjusted with different sealer finishes for safety requirements' }
  ];

  // Sample YouTube video data - you can update these with actual video IDs
  const youtubeVideos = [
    {
      id: 'UdBBK2Fzq8Y', // Actual video from your channel
      title: 'Grind & Seal: Commercial Warehouse Project',
      description: 'See our team complete a large-scale grind and seal installation'
    },
    {
      id: '9cIlr_oqnxI', // Actual video from your channel
      title: 'Maintenance Tips for Grind & Seal Floors',
      description: 'How to maintain your grind and seal floors for maximum lifespan'
    },
    {
      id: 'AYr7l4SCf3k', // Actual video from your channel
      title: 'Residential Garage Transformation',
      description: 'Transform your home garage with our grind and seal process'
    }
  ];

  // Sample YouTube Shorts data - you can update these with actual video IDs
  const youtubeShorts = [
    {
      id: 'HJkVxKwcD9Q', // Actual short from your channel
      title: 'Grind & Seal Process',
      description: 'Quick look at our sealing process'
    },
    {
      id: 'li9Gqu2Ev64', // Actual short from your channel
      title: 'Cost vs Quality',
      description: 'Why grind & seal is cost-effective'
    },
    {
      id: '3wf4cOZyBCg', // Actual short from your channel
      title: 'Sealer Application',
      description: 'Professional sealing technique'
    },
    {
      id: 'm8iC1KUli5M', // Actual short from your channel
      title: 'Aggregate Exposure',
      description: 'Full aggregate grind & seal'
    },
    {
      id: '-epF0_d1nVU', // Actual short from your channel
      title: 'Satin Finish Demo',
      description: 'Water based sealer finish'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 dark:from-slate-900 dark:via-slate-800 dark:to-slate-700">
      {/* Hero Video Section */}
      <section className="relative h-[70vh] overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          data-testid="grind-seal-hero-video"
        >
          <source src={service.video} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        {/* Video Overlay */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" />
        
        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
            className="max-w-4xl"
          >
            <Badge variant="secondary" className="mb-4 bg-white/20 text-white border-white/30">
              Cost-Effective Solution
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
              Grind &
              <span className="bg-gradient-to-r from-blue-300 to-cyan-400 bg-clip-text text-transparent block">
                Seal
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-100 mb-8 max-w-2xl mx-auto leading-relaxed">
              The practical choice for concrete polishing. Great results with budget-conscious upfront investment.
            </p>
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white border-0 shadow-2xl"
              asChild
            >
              <Link to="/contact" data-testid="hero-cta-button">
                Get Free Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        {/* Introduction */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-100 dark:to-slate-300 bg-clip-text text-transparent">
              Smart Entry Point to Polished Concrete
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
              Grind and Seal represents another form of polished concrete that shares the same initial diamond grinding 
              process as MPC, but finishes with a protective topical sealer instead of mechanical polishing. This creates 
              an attractive, durable surface at a more accessible price point.
            </p>
            <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              While it's a cost-effective option initially, it's important to understand the long-term investment. 
              The topical sealer, while providing excellent protection and sheen, will require periodic renewal every 3-5 years 
              as it can scratch and wear with heavy use. This makes it potentially costlier over time compared to MPC.
            </p>
          </div>
        </motion.section>

        {/* Key Features */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Why Choose Grind & Seal?
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {keyFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950 dark:to-cyan-950">
                  <CardContent className="p-6 text-center">
                    <feature.icon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                    <h4 className="font-semibold mb-2 text-slate-800 dark:text-slate-100">
                      {feature.title}
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Honest Comparison */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Honest Comparison: Grind & Seal vs MPC
          </h3>
          <div className="max-w-4xl mx-auto">
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100 dark:bg-slate-800">
                      <tr>
                        <th className="p-4 text-left font-semibold text-slate-800 dark:text-slate-100">Aspect</th>
                        <th className="p-4 text-center font-semibold text-blue-700 dark:text-blue-300">Grind & Seal</th>
                        <th className="p-4 text-center font-semibold text-amber-700 dark:text-amber-300">MPC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {honestComparison.map((row, index) => (
                        <tr key={row.aspect} className={index % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-slate-50 dark:bg-slate-800'}>
                          <td className="p-4 font-medium text-slate-800 dark:text-slate-100">{row.aspect}</td>
                          <td className={`p-4 text-center text-sm ${row.winner === 'grind' ? 'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 font-medium' : 'text-slate-600 dark:text-slate-300'}`}>
                            {row.grindSeal}
                          </td>
                          <td className={`p-4 text-center text-sm ${row.winner === 'mpc' ? 'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 font-medium' : 'text-slate-600 dark:text-slate-300'}`}>
                            {row.mpc}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-950 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-1">Long-term Investment Consideration</h4>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    While grind & seal costs less initially, the 3-5 year resealing cycle can make it more expensive than MPC over a 10+ year period. Factor in downtime and maintenance costs when making your decision.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Applications */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-6 text-slate-800 dark:text-slate-100">
                Ideal Applications
              </h3>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-8">
                Grind and seal works exceptionally well for spaces where budget is a primary concern or where 
                future renovations are planned. It's still a fantastic flooring solution that provides excellent 
                protection and aesthetics for the right applications.
              </p>
              <div className="grid gap-3">
                {idealApplications.map((app, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-slate-700 dark:text-slate-200">{app}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <Card className="p-6 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 border-slate-300 dark:border-slate-600">
                <h4 className="text-xl font-semibold mb-4 text-slate-800 dark:text-slate-100">
                  When Grind & Seal Makes Sense
                </h4>
                <ul className="space-y-3 text-slate-600 dark:text-slate-300">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Budget constraints require lower upfront investment</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Temporary or short-term facility usage</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Quick project turnaround required</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Moderate traffic environments</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </motion.section>

        {/* Technical Details */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Technical Process & Details
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {technicalDetails.map((detail, index) => (
              <motion.div
                key={detail.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-slate-200 dark:border-slate-700">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <Diamond className="w-6 h-6 text-blue-600 flex-shrink-0" />
                      <h4 className="font-semibold text-slate-800 dark:text-slate-100">
                        {detail.title}
                      </h4>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {detail.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Grind & Seal Project Gallery Carousel */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Grind & Seal Project Gallery
          </h3>
          <div className="max-w-4xl mx-auto">
            <Card className="overflow-hidden">
              <div className="relative">
                {/* Main Image Display */}
                <div className="aspect-video relative overflow-hidden bg-slate-100 dark:bg-slate-800">
                  <img
                    src={allGrindSealImages[currentImageIndex].src}
                    alt={allGrindSealImages[currentImageIndex].title}
                    className="w-full h-full object-cover transition-opacity duration-500"
                    data-testid="grind-seal-carousel-main-image"
                  />
                  
                  {/* Navigation Arrows */}
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                    data-testid="grind-seal-carousel-prev-button"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                    data-testid="grind-seal-carousel-next-button"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>

                  {/* Image Counter */}
                  <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                    {currentImageIndex + 1} / {allGrindSealImages.length}
                  </div>
                </div>

                {/* Image Info */}
                <div className="p-6 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950 dark:to-cyan-950">
                  <h4 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                    {allGrindSealImages[currentImageIndex].title}
                  </h4>
                  <p className="text-slate-600 dark:text-slate-300">
                    {allGrindSealImages[currentImageIndex].description}
                  </p>
                </div>

                {/* Thumbnail Strip */}
                <div className="p-4 bg-slate-100 dark:bg-slate-800">
                  <div className="flex space-x-2 overflow-x-auto pb-2">
                    {allGrindSealImages.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                          index === currentImageIndex 
                            ? 'border-blue-500 dark:border-blue-400' 
                            : 'border-transparent hover:border-slate-300 dark:hover:border-slate-600'
                        }`}
                        data-testid={`grind-seal-thumbnail-${index}`}
                      >
                        <img
                          src={image.src}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* Carousel Controls */}
            <div className="text-center mt-6">
              <p className="text-slate-600 dark:text-slate-300 mb-4">
                {allGrindSealImages.length} grind and seal projects showcasing cost-effective quality solutions
              </p>
              <div className="flex justify-center space-x-4">
                <Button
                  variant="outline"
                  onClick={prevImage}
                  className="border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-950"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" /> Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={nextImage}
                  className="border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-950"
                >
                  Next <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </motion.section>

        {/* YouTube Videos */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Watch Grind & Seal in Action
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {youtubeVideos.map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="relative aspect-video">
                    <iframe
                      src={`https://www.youtube.com/embed/${video.id}?hd=1&vq=hd1080&rel=0&modestbranding=1`}
                      title={video.title}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      data-testid={`youtube-video-${index}`}
                    />
                  </div>
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-2 text-slate-800 dark:text-slate-100">
                      {video.title}
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {video.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button
              variant="outline"
              size="lg"
              className="border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-950"
              onClick={() => window.open(COMPANY_INFO.youtube, '_blank')}
              data-testid="view-all-videos-button"
            >
              View All Videos on YouTube <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </motion.section>

        {/* YouTube Shorts */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Quick Grind & Seal Tips - YouTube Shorts
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
            {youtubeShorts.map((short, index) => (
              <motion.div
                key={short.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="relative aspect-[9/16]">
                    <iframe
                      src={`https://www.youtube.com/embed/${short.id}?hd=1&vq=hd1080&rel=0&modestbranding=1`}
                      title={short.title}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                    {/* Shorts Label */}
                    <div className="absolute top-3 left-3 bg-red-600 text-white px-2 py-1 text-xs rounded font-medium pointer-events-none z-10">
                      Shorts
                    </div>
                    {/* Share indicator */}
                    <div className="absolute top-3 right-3 bg-black/70 text-white p-1.5 rounded pointer-events-none z-10">
                      <Share className="w-3 h-3" />
                    </div>
                    {/* Share indicator */}
                    <div className="absolute top-3 right-3 bg-black/70 text-white p-1.5 rounded pointer-events-none z-10">
                      <Share className="w-3 h-3" />
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-2 text-slate-800 dark:text-slate-100">
                      {short.title}
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {short.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button
              variant="outline"
              size="lg"
              className="border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-950"
              onClick={() => window.open(COMPANY_INFO.youtube, '_blank')}
              data-testid="view-all-shorts-button"
            >
              View All Shorts on YouTube <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </motion.section>

        {/* Contact Form Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="max-w-6xl mx-auto">
            <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
              Request Your Grind & Seal Quote
            </h3>
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <Card className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="grind-seal-contact-form">
                  <div>
                    <Label htmlFor="name" className="text-slate-700 dark:text-slate-200">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your full name"
                      className="mt-1"
                      data-testid="contact-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-slate-700 dark:text-slate-200">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@example.com"
                      className="mt-1"
                      data-testid="contact-email-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 dark:text-slate-200">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+614 XX XXX XXX"
                      className="mt-1"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="message" className="text-slate-700 dark:text-slate-200">Project Details *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us about your grind & seal project - space type, size, timeline, and budget considerations..."
                      rows={5}
                      className="mt-1"
                      data-testid="contact-message-textarea"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white border-0"
                    data-testid="contact-submit-button"
                  >
                    {isSubmitting ? 'Sending...' : (
                      <>
                        Send Grind & Seal Inquiry <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </Card>
              
              {/* Contact Information */}
              <div className="space-y-8">
                <Card className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950 dark:to-cyan-950 border-blue-200 dark:border-blue-800">
                  <h4 className="text-xl font-semibold mb-4 text-slate-800 dark:text-slate-100">
                    Cost-Effective Specialists
                  </h4>
                  <p className="text-lg text-slate-600 dark:text-slate-300 mb-6">
                    We provide honest advice about when grind & seal is the right choice for your project, 
                    including long-term cost considerations and maintenance requirements.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Mail className="w-5 h-5 text-blue-600" />
                      <span className="text-slate-700 dark:text-slate-200">{COMPANY_INFO.email}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Phone className="w-5 h-5 text-blue-600" />
                      <span className="text-slate-700 dark:text-slate-200">{COMPANY_INFO.phone}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <MapPin className="w-5 h-5 text-blue-600" />
                      <span className="text-slate-700 dark:text-slate-200">{COMPANY_INFO.address}</span>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-6">
                  <h4 className="text-xl font-semibold mb-4 text-slate-800 dark:text-slate-100">
                    What to Expect
                  </h4>
                  <div className="space-y-3 text-slate-600 dark:text-slate-300">
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Honest assessment of grind & seal vs MPC for your needs</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Clear breakdown of initial and long-term costs</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Detailed maintenance schedule and requirements</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Professional workmanship with warranty</span>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Final CTA Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Card className="p-12 bg-gradient-to-r from-slate-900 to-slate-800 dark:from-slate-100 dark:to-slate-200 text-white dark:text-slate-900">
            <DollarSign className="w-16 h-16 text-blue-400 dark:text-blue-600 mx-auto mb-6" />
            <h3 className="text-3xl font-bold mb-4">
              Smart Choice for Budget-Conscious Projects
            </h3>
            <p className="text-lg mb-8 max-w-2xl mx-auto opacity-90">
              Grind & seal offers excellent value for the right applications. Let us help you determine 
              if it's the best fit for your project and budget.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white border-0"
                onClick={() => document.querySelector('[data-testid="grind-seal-contact-form"]').scrollIntoView({ behavior: 'smooth' })}
                data-testid="final-cta-contact-button"
              >
                Get Your Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="border-white text-white hover:bg-white hover:text-slate-900 dark:border-slate-900 dark:text-slate-900 dark:hover:bg-slate-900 dark:hover:text-white"
                asChild
              >
                <Link to="/services/polished-concrete" data-testid="final-cta-compare-button">
                  Compare with MPC
                </Link>
              </Button>
            </div>
          </Card>
        </motion.section>
      </div>
    </div>
  );
};

export default GrindAndSealPage;